/**
 * Shared Styled Table Components
 * 
 * This file contains reusable MUI styled table components used across
 * TimeLogTable and ClientSummaryTable to eliminate code duplication
 * and ensure consistent styling.
 * 
 * All colors are pulled from the MUI theme (theme.palette.table)
 * defined in react/config/theme.tsx
 */

import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { styled } from '@mui/material/styles';

/**
 * Styled Table Cell - Used for both header and body cells
 * 
 * Features:
 * - Dark header background with white text
 * - Compact padding (5px)
 * - Responsive font sizing
 */
export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.table.header,
    color: theme.palette.table.headerText,
    padding: theme.spacing(0.625), // 5px (0.625 * 8px)
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: theme.spacing(0.625), // 5px
  },
}));

/**
 * Styled Table Cell for Footer Rows
 * 
 * Features:
 * - Same dark background as header
 * - White text
 * - Bold font weight for emphasis
 */
export const StyledTableCellFooter = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    backgroundColor: theme.palette.table.footer,
    fontSize: 14,
    padding: theme.spacing(0.625), // 5px
    color: theme.palette.table.footerText,
    fontWeight: 'bold',
  },
}));

/**
 * Styled Table Row
 * 
 * Features:
 * - Alternating row colors (odd rows white)
 * - No border on last row
 */
export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.table.rowOdd,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

